import { FC, ReactNode } from 'react';

import { Breakpoint, Theme, useMediaQuery } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import ImageEmpty from '@o2/icons/ImageEmpty';
import Paper, { PaperProps } from '@o2/ui/Paper';
import Stack from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';

export interface EmptyStatePaneProps {
  /**
   * Background color of the pane.
   */
  color?: PaperProps['color'];

  /**
   * What breakpoint to start putting the text next to the image instead of under it and when to resize the image to 100px
   * (instead of 72px, like we do on XS).
   */
  largeStyleFrom?: Breakpoint;

  /**
   * When embedded in e.g. another Paper, this prop allows setting negative margins of the Paper INSIDE this component, so that
   * spacings can be driven by this component only. In other words, pass the spacing of the CONTAINING element here in order
   * to remove it.
   *
   * When passing custom values, please only use theme units as numbers.
   *
   * The default is to do nothing.
   */
  negativeMargin?: ResponsiveStyleValue<number>;

  /**
   * Text to render.
   */
  text: ReactNode;

  /**
   * Size of the image.
   */
  imageSize?: keyof typeof imageSizes;
}

/**
 * Sizes for ImageEmpty illustration image
 */
const imageSizes = {
  default: '100px',
  small: '72px',
};

/**
 * This component can be used, for example, as a replacement for tables which have no data.
 */
const EmptyStatePane: FC<EmptyStatePaneProps> = ({
  text,
  imageSize,
  largeStyleFrom = 'md',
  negativeMargin,
  ...props
}) => {
  const largeStyle = useMediaQuery((theme: Theme) => theme.breakpoints.up(largeStyleFrom));
  const size = imageSize === 'small' ? imageSizes.small : largeStyle ? imageSizes.default : imageSizes.small;
  const margins =
    negativeMargin &&
    // if an object, negate all numbers passed
    (typeof negativeMargin === 'object'
      ? Object.entries(negativeMargin).reduce((prev, [breakpoint, value]) => {
          return { ...prev, [breakpoint]: value === null ? null : -value };
        }, {})
      : // otherwise just negate the number
        -negativeMargin);

  return (
    <Paper p={(theme: Theme) => theme.spacing(2, 4)} sx={margins ? { m: margins } : {}} {...props}>
      <Stack direction={{ xs: 'column', [largeStyleFrom]: 'row' }} alignItems="center" gap={2}>
        <ImageEmpty style={{ maxWidth: size, maxHeight: size }} />
        <Typography mb={{ xs: 2, [largeStyleFrom]: 0 }} textAlign={largeStyle ? 'left' : 'center'}>
          {text}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default EmptyStatePane;
